import { DiffPatcher } from 'jsondiffpatch';
import type {
  AgentBuilderHistoryEntry,
  PersistedDiff,
  PersistedEditorHistory,
  WorkflowSnapshot,
} from '@/agentBuilder/agentBuilder.types';

/**
 * Owns the diff/patch/hash logic for persisted agent builder editor history.
 * The rest of the codebase deals in `AgentBuilderHistoryEntry` (full snapshots);
 * this module is the only place that knows about jsondiffpatch.
 *
 * Invariant: any state rehydrated from a `PersistedEditorHistory` payload
 * must be field-equivalent to the snapshot it was encoded from.
 */

const diffPatcher = new DiffPatcher({
  // Match nodes and edges by `id` so reordering doesn't blow up array diffs.
  objectHash: (item: { id?: string }) => item?.id,
  arrays: { detectMove: true, includeValueOnMove: false },
});

const TEXT_ENCODER = typeof TextEncoder !== 'undefined' ? new TextEncoder() : null;

const stableStringify = (value: unknown): string => {
  const seen = new WeakSet<object>();
  const sortKeys = (input: unknown): unknown => {
    if (input === null || typeof input !== 'object') {
      return input;
    }
    if (seen.has(input as object)) {
      return null;
    }
    seen.add(input as object);
    if (Array.isArray(input)) {
      return input.map(sortKeys);
    }
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(input as Record<string, unknown>).sort()) {
      out[key] = sortKeys((input as Record<string, unknown>)[key]);
    }
    return out;
  };
  return JSON.stringify(sortKeys(value));
};

const bytesToHex = (bytes: Uint8Array): string => {
  let hex = '';
  for (let i = 0; i < bytes.length; i += 1) {
    hex += bytes[i].toString(16).padStart(2, '0');
  }
  return hex;
};

const fallbackHash = (serialized: string): string => {
  let h = 0;
  for (let i = 0; i < serialized.length; i += 1) {
    h = (h * 31 + serialized.charCodeAt(i)) | 0;
  }
  return `fallback-${(h >>> 0).toString(16)}-${serialized.length}`;
};

/**
 * Normalize a workflow snapshot to the stable subset that identifies a
 * workflow, projecting each node and edge down to an **allowlist** of
 * persistence-relevant fields.
 *
 * Why an allowlist rather than stripping known runtime fields: React Flow
 * injects rendering state that is not stable across the
 * save→localStorage→reload cycle — `measured`/`width`/`height`/`selected` on
 * nodes, and an expanded `markerEnd` object on edges (null/absent until the
 * next render pass). A denylist would have to enumerate every such field and
 * would silently break the moment React Flow adds a new one. Keeping only the
 * fields we care about means any unknown future runtime field is excluded by
 * default. This mirrors the approach in `buildAgentBuilderWorkflowSignature`
 * (the autosave dirty-check), which projects the same way for the same reason.
 *
 * Unlike `buildAgentBuilderWorkflowSignature`, we keep the FULL node/edge
 * `data` (minus the runtime `status` on nodes) because undo must restore every
 * semantic field — including classify-edge `categoryLabel`/`categoryIndex`,
 * which the dirty-check signature intentionally drops.
 *
 * Applied to BOTH diff inputs and hash inputs so the whole persistence layer
 * operates on one canonical shape; the save→reload cycle then compares like
 * with like. (CRAB-58719: `markerEnd` divergence was breaking the anchor check
 * on every reload.)
 */
export const normalizeSnapshotForPersistence = (snapshot: WorkflowSnapshot): WorkflowSnapshot => {
  const normalizeData = (data: unknown): unknown => {
    if (data === null || typeof data !== 'object' || Array.isArray(data)) {
      return data;
    }
    const copy = { ...(data as Record<string, unknown>) };
    delete copy.status; // runtime execution-state field; reset on reload
    return copy;
  };

  const nodes = snapshot.nodes.map((n) => ({
    id: n.id,
    type: n.type,
    position: { x: n.position?.x ?? 0, y: n.position?.y ?? 0 },
    data: normalizeData(n.data),
    // `hidden` carries context-visibility state (toggled via
    // handleToggleContextVisibility) and is server-round-tripped — normalizeNodes
    // preserves it on load — so it's semantic, not a volatile render field. Keep
    // it (when defined) so cross-session undo can restore visibility toggles.
    ...(n.hidden !== undefined && { hidden: n.hidden }),
  })) as unknown as WorkflowSnapshot['nodes'];

  const edges = snapshot.edges.map((e) => ({
    id: e.id ?? '',
    source: e.source,
    target: e.target,
    type: e.type,
    sourceHandle: e.sourceHandle ?? null,
    targetHandle: e.targetHandle ?? null,
    data: e.data ?? null,
    // Same as nodes: context-edge visibility lives on `hidden` and is
    // server-round-tripped (see normalizeEdges), so keep it when defined.
    ...(e.hidden !== undefined && { hidden: e.hidden }),
  })) as unknown as WorkflowSnapshot['edges'];

  return { nodes, edges };
};

/**
 * Deterministic content hash of a workflow snapshot. Normalizes via
 * `normalizeSnapshotForPersistence` first so the hash is over the stable
 * allowlisted subset (encode and decode both normalize before hashing).
 * Prefers Web Crypto SHA-256 when functional; falls back to a stable string
 * hash if the platform's subtle crypto throws (some Jest polyfills advertise
 * support but reject 'SHA-256').
 */
export async function hashWorkflowSnapshot(snapshot: WorkflowSnapshot): Promise<string> {
  const normalized = normalizeSnapshotForPersistence(snapshot);
  const serialized = stableStringify({ nodes: normalized.nodes, edges: normalized.edges });
  if (TEXT_ENCODER && typeof crypto !== 'undefined' && crypto.subtle) {
    try {
      const buffer = await crypto.subtle.digest('SHA-256', TEXT_ENCODER.encode(serialized));
      return bytesToHex(new Uint8Array(buffer));
    } catch {
      return fallbackHash(serialized);
    }
  }
  return fallbackHash(serialized);
}

const cloneSnapshot = (snapshot: WorkflowSnapshot): WorkflowSnapshot =>
  JSON.parse(JSON.stringify(snapshot)) as WorkflowSnapshot;

const entryToSnapshot = (entry: AgentBuilderHistoryEntry): WorkflowSnapshot =>
  normalizeSnapshotForPersistence({ nodes: entry.nodes, edges: entry.edges });

/**
 * Compute the diff chain that anchors past undo entries against the live
 * canvas. The chain has the shape:
 *
 *   past[0] → past[1] → ... → past[N-1] → canvas
 *
 * The oldest past entry (`past[0]`) is the implicit anchor at the start of
 * the chain — it isn't preceded by a diff. The trailing `past[N-1] → canvas`
 * diff is the load-time anchor at the end: its `resultHash` is the hash of
 * the canvas, so on rehydrate the decoder can verify the chain belongs to
 * the canvas it's about to walk back from.
 *
 * The `firstEntryPredecessor` parameter is currently unused for chain
 * encoding (the chain anchors on past[0] directly), but is retained in the
 * signature to keep the autosave call site stable; it may be reintroduced
 * if the persistence cap is ever raised above the in-memory cap, where the
 * predecessor would represent an in-memory entry just outside the persisted
 * window.
 *
 * Each adjacent pair is validated with a round-trip self-check
 * (`patch(prev, diff)` must equal `next`); a single failure returns null
 * so the caller can skip persistence rather than write data we can't
 * verify.
 *
 * @param boundedPast Up to PERSIST_PAST_LIMIT pre-mutation undo entries,
 *   oldest → newest. May be empty.
 * @param firstEntryPredecessor Retained for signature stability; not used.
 * @param currentCanvasState The live canvas — the conceptual "present" the
 *   chain anchors against. Becomes the terminal state of the encoded chain.
 */
export async function encodeHistoryForStorage(
  boundedPast: AgentBuilderHistoryEntry[],

  firstEntryPredecessor: WorkflowSnapshot,
  currentCanvasState: WorkflowSnapshot,
): Promise<PersistedEditorHistory | null> {
  // Normalize the canvas to the same stable shape the past entries use, so
  // the diff/hash/round-trip all operate on one canonical form.
  const normalizedCanvas = normalizeSnapshotForPersistence(currentCanvasState);

  // Empty past: nothing to encode (no edits this session). Canvas is the
  // implicit "now" and rehydrate will reconstruct an empty undo stack.
  if (boundedPast.length === 0) {
    return { lastWrittenAt: Date.now(), diffs: [] };
  }

  // Build the linear chain of states, ordered oldest → newest:
  //   states[0] = past[0]
  //   states[1] = past[1]
  //   ...
  //   states[N-1] = past[N-1]
  //   states[N]   = canvas (the conceptual "present")
  //
  // We emit one diff per *distinct* transition `states[i-1] → states[i]`.
  // jsondiffpatch returns undefined when two states are identical (after
  // normalization); we skip those — identical adjacent states are
  // indistinguishable to the user, so collapsing them is harmless. Each diff
  // carries the recordedAt of its destination state.
  type Step = { thisState: WorkflowSnapshot; recordedAt: number };
  const states: Step[] = boundedPast.map((entry) => ({
    thisState: cloneSnapshot(entryToSnapshot(entry)),
    recordedAt: entry.recordedAt,
  }));
  states.push({ thisState: cloneSnapshot(normalizedCanvas), recordedAt: Date.now() });

  const diffs: PersistedDiff[] = [];
  // The chain anchors on past[0]; previousState starts there and walks forward.
  let previousState: WorkflowSnapshot = cloneSnapshot(entryToSnapshot(boundedPast[0]));

  // Skip states[0] (= past[0]) since it's the chain anchor and has no
  // incoming diff. Process transitions for i = 1..N. Identical adjacent
  // states (after normalization) produce no diff and are collapsed; only
  // distinct transitions are recorded.
  for (let i = 1; i < states.length; i += 1) {
    const { thisState, recordedAt } = states[i];
    const patch = diffPatcher.diff(previousState, thisState);

    if (patch !== undefined) {
      // Round-trip self-check: applying the patch must reproduce thisState.
      // Catches jsondiffpatch corner cases / our own bugs at write time.
      const reconstructed = diffPatcher.patch(cloneSnapshot(previousState), patch);
      if (stableStringify(reconstructed) !== stableStringify(thisState)) {
        return null;
      }
      diffs.push({
        patch: patch as Record<string, unknown>,
        resultHash: await hashWorkflowSnapshot(thisState),
        recordedAt,
      });
    }

    previousState = thisState;
  }

  return { lastWrittenAt: Date.now(), diffs };
}

/**
 * The result of a successful decode: the reconstructed past entries (oldest
 * → newest) and the workflow state immediately *before* the oldest entry,
 * which the autosave layer needs as the predecessor anchor for any future
 * persistence writes until enough new entries accumulate to push the old
 * ones out of the window.
 */
export type DecodedHistory = {
  pastEntries: AgentBuilderHistoryEntry[];
  predecessorState: WorkflowSnapshot;
};

/**
 * Walk the diff chain backwards from the current canvas state to reconstruct
 * the past undo entries. Returns null on any integrity failure (anchor
 * mismatch, intermediate hash mismatch, unpatch throw, shape problem) so the
 * caller can clear the bad payload and start with an empty in-memory stack.
 *
 * The persisted chain has N diffs over N+1 states:
 *   past[0] → past[1] → ... → past[N-1] → canvas
 * The newest diff's `resultHash` equals `hash(canvas)` by construction
 * (encode normalizes the canvas the same way), so the anchor check is a
 * structural invariant — not an off-by-one tripwire.
 *
 * Walking backwards from the (normalized) canvas through unpatch recovers
 * each past entry in turn. The oldest recovered state (past[0]) is also
 * returned as `predecessorState` so the autosave layer has an anchor for
 * future writes.
 */
export async function decodeHistoryFromStorage(
  payload: unknown,
  currentCanvasState: WorkflowSnapshot,
): Promise<DecodedHistory | null> {
  if (!isPersistedEditorHistory(payload)) {
    return null;
  }

  // Normalize the canvas so the anchor check and the backwards walk compare
  // against the same stable shape encode used (strips React Flow runtime
  // fields like `measured` on nodes and the expanded `markerEnd` on edges).
  const normalizedCanvas = normalizeSnapshotForPersistence(currentCanvasState);

  const { diffs } = payload;
  if (diffs.length === 0) {
    return { pastEntries: [], predecessorState: cloneSnapshot(normalizedCanvas) };
  }

  // Anchor check: the canvas must match the newest persisted state.
  const canvasHash = await hashWorkflowSnapshot(normalizedCanvas);
  const newestDiff = diffs[diffs.length - 1];
  if (canvasHash !== newestDiff.resultHash) {
    return null;
  }

  // Walking backwards from canvas through unpatch recovers N states:
  //   diff[N-1] unpatched gives past[N-1]
  //   diff[N-2] unpatched against past[N-1] gives past[N-2]
  //   ...
  //   diff[0]   unpatched against past[1]   gives past[0]
  // Every recovered state IS a past entry. The oldest one (past[0]) is also
  // returned as predecessorState so the autosave layer has an anchor for
  // future writes.
  const reconstructedReversed: AgentBuilderHistoryEntry[] = [];
  let state: WorkflowSnapshot = cloneSnapshot(normalizedCanvas);

  for (let i = diffs.length - 1; i >= 0; i -= 1) {
    const diff = diffs[i];

    let previousStateInChain: WorkflowSnapshot;
    try {
      previousStateInChain = diffPatcher.unpatch(cloneSnapshot(state), diff.patch) as WorkflowSnapshot;
    } catch {
      return null;
    }

    // Chain-integrity check: when i > 0, the state we just unpatched into is
    // the post-state of diffs[i-1], so its hash must match diffs[i-1].resultHash.
    // When i = 0 we've recovered past[0] (the chain's implicit anchor) and
    // there's no earlier diff to verify against.
    if (i > 0) {
      const expectedHash = diffs[i - 1].resultHash;
      const actualHash = await hashWorkflowSnapshot(previousStateInChain);
      if (expectedHash !== actualHash) {
        return null;
      }
    }

    // Every recovered state IS a past entry. recordedAt: for i > 0 the diff
    // that landed on this state is diffs[i-1]; for i = 0 (past[0]) the
    // original recordedAt isn't separately preserved, so use the oldest
    // diff's recordedAt as the closest surrogate.
    const recordedAtForEntry = i > 0 ? diffs[i - 1].recordedAt : diffs[0].recordedAt;
    reconstructedReversed.push({
      nodes: previousStateInChain.nodes,
      edges: previousStateInChain.edges,
      recordedAt: recordedAtForEntry,
    });

    state = previousStateInChain;
  }

  // The reconstructed entries were pushed newest-first; reverse to oldest-first.
  const pastEntries = reconstructedReversed.reverse();
  return {
    pastEntries,
    predecessorState: { nodes: pastEntries[0].nodes, edges: pastEntries[0].edges },
  };
}

const isPersistedEditorHistory = (value: unknown): value is PersistedEditorHistory => {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Partial<PersistedEditorHistory>;
  if (typeof candidate.lastWrittenAt !== 'number' || !Array.isArray(candidate.diffs)) {
    return false;
  }
  return candidate.diffs.every(
    (d) =>
      d !== null &&
      typeof d === 'object' &&
      typeof (d as PersistedDiff).resultHash === 'string' &&
      typeof (d as PersistedDiff).recordedAt === 'number' &&
      (d as PersistedDiff).patch !== null &&
      typeof (d as PersistedDiff).patch === 'object',
  );
};
